import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { Button, CustomDialog } from 'web-library';
import { ButtonVariant } from 'web-library/stories/atom/Button/Button';
import PaymentMethodsModal from './PaymentMethodsModal';
import PaymentErrorModal from './PaymentErrorModal';
import { useCustomMutate } from '../../../hooks/query';
import { postPaymentOffline } from '../../../api/financial/api';
import { useENetsWindowEventListener, usePayNowPolling } from '../logic/usePaymentButton';
import QRPaymentModal from './QRPaymentModal';
import ENetsModal from './ENetsModal';
import BankTransferModal from './BankTransferModal';
import { useUserDetails } from '../../../hooks/userDetails';
import { Role } from '../../../models';
import { useCoordinatorResit } from '../../resit/logic/useCoordinatorResit';
import RouteKey from '../../../routes/key';
import PaymentApiButton from './PaymentApiButton';
import { ApplicationType } from '../../../api/candidate/config/model';
import { usePaymentState } from '../logic/usePaymentState';

const { coordinatorExamRegistrations, examRegistrations } = RouteKey.exam.child;
const { applications, applicationsCoordinator } = RouteKey.appeal.child;
const { amendment, appeal, exam } = RouteKey.child;

export enum PaymentMethod {
  ENETS_CREDIT_CARD = 'ENETS_CREDIT_CARD',
  BANK_TRANSFER = 'BANK_TRANSFER',
  PAYNOW_QR = 'PAYNOW_QR',
}

export const SET_ERROR_ACTION = 'SET_ERROR_ACTION';
export const RESET_ERROR_ACTION = 'RESET_ERROR_ACTION';
export const REST_ERROR_WITH_RETRY_ACTION = 'REST_ERROR_WITH_RETRY_ACTION';

interface PaymentErrorState {
  isErrorModalOpen: boolean;
  isRetry?: boolean;
  errorMessages?: string[];
}

interface SetErrorAction {
  type: typeof SET_ERROR_ACTION;
  payload: PaymentErrorState;
}

interface ResetErrorAction {
  type: typeof RESET_ERROR_ACTION;
}

interface ResetErrorActionWithRetry {
  type: typeof REST_ERROR_WITH_RETRY_ACTION;
}

export type PaymentErrorAction = SetErrorAction | ResetErrorAction | ResetErrorActionWithRetry;

interface PaymentButtonProps {
  buttonClassName?: string;
  onHandlePaymentMethod: (
    callback: (txnId: string, error?: string, errorMsg?: string) => void,
    paymentMethod: string,
    paymentModalOnClose?: (paymentMethod: PaymentMethod) => void
  ) => void;
  onClickPaymentButton: (callback: () => void) => void;
  onSuccessPayment: (txnId: string) => void;
  paymentMethodsAvailable?: Array<PaymentMethod>;
  isUniqueLink: boolean;
  disabled?: boolean;
  onPaymentComplete?: (retry: boolean) => void;
  buttonText?: string;
  buttonErrorText?: string;
  fullWidth?: boolean;
  buttonVariant?: ButtonVariant;
  type?: ApplicationType;
  isLoading?: boolean;
  customPaymentMessage?: string;
  vacancyExpiredModal?: React.ReactNode;
}

export const PAYMENT_ERROR_INITIAL_STATE: PaymentErrorState = {
  isErrorModalOpen: false,
  isRetry: undefined,
  errorMessages: undefined,
};

export const paymentErrorReducer = (
  state: PaymentErrorState,
  action: PaymentErrorAction
): PaymentErrorState => {
  switch (action.type) {
    case SET_ERROR_ACTION:
      return { ...state, ...action.payload };
    case REST_ERROR_WITH_RETRY_ACTION:
      return { ...state, isRetry: false };
    case RESET_ERROR_ACTION:
    default:
      return PAYMENT_ERROR_INITIAL_STATE;
  }
};

const UNEXPECTED_ERROR_ACTION: PaymentErrorAction = {
  type: SET_ERROR_ACTION,
  payload: {
    isErrorModalOpen: true,
    isRetry: false,
    errorMessages: [
      'An unexpected error occurred. Please try again. Please finish the pending payment if you have or redo from start.',
    ],
  },
};

const isApplicationTypeAppeal = (type?: ApplicationType) => type && type === ApplicationType.APPEAL;
const isApplicationTypeResit = (type?: ApplicationType) => type && type === ApplicationType.RESIT;

const handleCoordinatorRoute = (
  userDetails: any,
  history: RouteComponentProps['history'],
  stepNavigate: any,
  type?: ApplicationType
) => {
  if (userDetails.data.roles?.includes(Role.coordinator)) {
    if (isApplicationTypeAppeal(type)) {
      history.push(`/${appeal}/${applicationsCoordinator.route}`);
    } else if (isApplicationTypeResit(type)) {
      history.push(`/${amendment}/${coordinatorExamRegistrations.route}`);
    } else {
      stepNavigate(false);
    }
  }
};

const NoRetryStatusHandling = (
  userDetails: any,
  stepNavigate: any,
  history: any,
  retry: boolean,
  type?: ApplicationType
) => {
  if (!retry && userDetails && userDetails.data) {
    if (userDetails.data.roles?.includes(Role.candidate)) {
      // temporarily fix for exam registration and appeal, need fix here for resit in the future
      if (isApplicationTypeAppeal(type)) {
        history.push(`/${appeal}/${applications.route}`);
      } else {
        history.push(`/${exam}/${examRegistrations.route}`);
      }
    }
    handleCoordinatorRoute(userDetails, history, stepNavigate, type);
  }
};

const openCheckoutWindow = (internalTransactionRef: string): Window | null =>
  window.open(
    `${import.meta.env.VITE_APP_PAYMENT_REDIRECT_DOMAIN}/financial/api/nets/checkout?paymentMode=NETS_CC&internalTransactionRef=${internalTransactionRef}`,
    'checkout',
    'menubar=no,toolbar=no,scrollbars=yes'
  );

const checkChildWindowClosed = (
  windowObject: Window | null,
  setIsENetsModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  clearInterval: (handle?: number) => void,
  pollChildWindow: any
) => {
  if (windowObject && windowObject.closed) {
    setIsENetsModalOpen(false);
    clearInterval(pollChildWindow);
  }
};

const usePaymentButtonHooks = () => {
  const { data: userDetails } = useUserDetails();
  const history = useHistory();
  const { stepNavigate } = useCoordinatorResit(false);

  const [enetsWindowObjectReference, setEnetsWindowObjectReference] = useState<Window | null>(null);
  const [isENetsModalOpen, setIsENetsModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [isQRPaymentModalOpen, setIsQRPaymentModalOpen] = useState(false);
  const [isBankTransferModalOpen, setIsBankTransferModalOpen] = useState(false);

  const { mutate, status: statusPaymentOffline } = useCustomMutate(
    'POST_MANUAL_BANK_TRANSFER',
    postPaymentOffline
  );

  const [{ isErrorModalOpen, isRetry, errorMessages }, paymentErrorDispatch] = useReducer(
    paymentErrorReducer,
    PAYMENT_ERROR_INITIAL_STATE
  );

  const {
    internalTransactionRef,
    selectedPaymentMethod,
    isDisabled,
    pollingEnabled,
    setInternalTransactionRef,
    setSelectedPaymentMethod,
    setIsDisabled,
    setPollingEnabled,
    paymentModalOnClose,
  } = usePaymentState(
    setIsENetsModalOpen,
    setIsQRPaymentModalOpen,
    setIsPaymentModalOpen,
    setIsBankTransferModalOpen
  );

  useEffect(() => {
    // open a new window instead of refocusing previous window when internalTransactionRef changed
    // because it should be treated as a different transaction
    setEnetsWindowObjectReference(null);
  }, [internalTransactionRef, setEnetsWindowObjectReference]);

  useEffect(() => {
    const pollChildWindow: any = setInterval(
      () =>
        checkChildWindowClosed(
          enetsWindowObjectReference,
          setIsENetsModalOpen,
          clearInterval,
          pollChildWindow
        ),
      200
    );

    return () => clearInterval(pollChildWindow);
  }, [enetsWindowObjectReference, setIsENetsModalOpen]);

  return {
    userDetails,
    history,
    stepNavigate,
    mutate,
    statusPaymentOffline,
    isErrorModalOpen,
    isRetry,
    errorMessages,
    paymentErrorDispatch,
    enetsWindowObjectReference,
    setEnetsWindowObjectReference,
    isENetsModalOpen,
    setIsENetsModalOpen,
    isPaymentModalOpen,
    setIsPaymentModalOpen,
    isConfirmationModalOpen,
    setIsConfirmationModalOpen,
    isQRPaymentModalOpen,
    setIsQRPaymentModalOpen,
    isBankTransferModalOpen,
    setIsBankTransferModalOpen,
    internalTransactionRef,
    selectedPaymentMethod,
    isDisabled,
    pollingEnabled,
    setInternalTransactionRef,
    setSelectedPaymentMethod,
    setIsDisabled,
    setPollingEnabled,
    paymentModalOnClose,
  };
};

/**
 * Do not unmount this component when payment is ongoing
 *
 * @param buttonClassName
 * @param onClickPaymentButton
 * @param onSuccessPayment
 * @param handlePaymentMethod
 * @param paymentMethodsAvailable
 * @param disabled
 */
const PaymentButton = ({
  buttonClassName = undefined,
  onClickPaymentButton = (callback) => {
    callback();
  },
  onHandlePaymentMethod = (callback) => {
    callback('');
  },
  onSuccessPayment,
  paymentMethodsAvailable = [
    PaymentMethod.ENETS_CREDIT_CARD,
    PaymentMethod.PAYNOW_QR,
    PaymentMethod.BANK_TRANSFER,
  ],
  disabled = undefined,
  isUniqueLink = false,
  onPaymentComplete = (retry: boolean) => null,
  buttonText,
  fullWidth = true,
  buttonVariant,
  buttonErrorText = 'Back To Registered Exam(s)',
  type,
  isLoading = false,
  customPaymentMessage,
  vacancyExpiredModal = <></>,
}: PaymentButtonProps) => {
  const [isVacancyExpired, setIsVacancyExpired] = useState(false);
  const {
    userDetails,
    history,
    stepNavigate,
    mutate,
    statusPaymentOffline,
    isErrorModalOpen,
    isRetry,
    errorMessages,
    paymentErrorDispatch,
    enetsWindowObjectReference,
    setEnetsWindowObjectReference,
    isENetsModalOpen,
    setIsENetsModalOpen,
    isPaymentModalOpen,
    setIsPaymentModalOpen,
    isConfirmationModalOpen,
    setIsConfirmationModalOpen,
    isQRPaymentModalOpen,
    setIsQRPaymentModalOpen,
    isBankTransferModalOpen,
    setIsBankTransferModalOpen,
    internalTransactionRef,
    selectedPaymentMethod,
    isDisabled,
    pollingEnabled,
    setInternalTransactionRef,
    setSelectedPaymentMethod,
    setIsDisabled,
    setPollingEnabled,
    paymentModalOnClose,
  } = usePaymentButtonHooks();

  // listen to data sent from ENetsPaymentInterimResult.tsx
  useENetsWindowEventListener(
    selectedPaymentMethod,
    internalTransactionRef,
    paymentErrorDispatch,
    onSuccessPayment,
    setIsPaymentModalOpen
  );

  const { useQrCodeMutate, regenerateQrCode, countDownDisplay, isQrExpired } = usePayNowPolling(
    selectedPaymentMethod,
    internalTransactionRef,
    pollingEnabled,
    isQRPaymentModalOpen,
    (value) => setPollingEnabled(value),
    () => onSuccessPayment(internalTransactionRef)
  );

  const closePaymentModals = useCallback(() => {
    setIsENetsModalOpen(false);
    setIsQRPaymentModalOpen(false);
    setIsBankTransferModalOpen(false);
    setIsPaymentModalOpen(false);
    setIsConfirmationModalOpen(false);
  }, [
    setIsBankTransferModalOpen,
    setIsConfirmationModalOpen,
    setIsENetsModalOpen,
    setIsPaymentModalOpen,
    setIsQRPaymentModalOpen,
  ]);

  const closeAllPaymentModals = useCallback(
    (retry: boolean, applicationType?: ApplicationType) => {
      closePaymentModals();
      NoRetryStatusHandling(userDetails, stepNavigate, history, retry, applicationType);
      paymentErrorDispatch({ type: RESET_ERROR_ACTION });
    },
    [closePaymentModals, history, paymentErrorDispatch, stepNavigate, userDetails]
  );

  return (
    <>
      <PaymentMethodsModal
        paymentMethodsAvailable={paymentMethodsAvailable}
        isDisabled={isDisabled}
        modelOpen={isPaymentModalOpen}
        onClose={() => {
          setIsDisabled(false);
          setIsPaymentModalOpen(false);
        }}
        onClickCheckout={(paymentMethod) => {
          const onClickPaymentCallback = (txnId: string, error?: string, errorMsg?: string) => {
            if (txnId) {
              setInternalTransactionRef(txnId);
              setIsPaymentModalOpen(false);
              setSelectedPaymentMethod(paymentMethod);
              setIsENetsModalOpen(PaymentMethod.ENETS_CREDIT_CARD === paymentMethod);
              setIsQRPaymentModalOpen(PaymentMethod.PAYNOW_QR === paymentMethod);
              setIsBankTransferModalOpen(PaymentMethod.BANK_TRANSFER === paymentMethod);
              if (!(paymentMethod in PaymentMethod)) {
                paymentErrorDispatch(UNEXPECTED_ERROR_ACTION);
              }
            } else if (error === '8210') {
              setIsVacancyExpired(true);
              setIsPaymentModalOpen(false);
            } else {
              paymentErrorDispatch(UNEXPECTED_ERROR_ACTION);
            }
          };
          setIsDisabled(true);
          onHandlePaymentMethod(onClickPaymentCallback, paymentMethod, paymentModalOnClose);
        }}
        customPaymentMessage={customPaymentMessage}
      />
      <ENetsModal
        modelOpen={isENetsModalOpen}
        onClose={() => paymentModalOnClose(PaymentMethod.ENETS_CREDIT_CARD)}
        onClickCheckout={() => {
          if (!enetsWindowObjectReference || enetsWindowObjectReference.closed) {
            const windowObject = openCheckoutWindow(internalTransactionRef);
            setEnetsWindowObjectReference(windowObject);
          } else {
            enetsWindowObjectReference.focus();
          }
        }}
      />
      <QRPaymentModal
        modelOpen={isQRPaymentModalOpen}
        onClose={() => paymentModalOnClose(PaymentMethod.PAYNOW_QR)}
        useQrCodeMutate={useQrCodeMutate}
        onClickRegenerateQrCode={regenerateQrCode}
        isQrExpired={isQrExpired}
        countDownDisplay={countDownDisplay}
      />
      <BankTransferModal
        modelOpen={isBankTransferModalOpen}
        onClose={() => paymentModalOnClose(PaymentMethod.BANK_TRANSFER)}
        onClickCheckout={() => {
          mutate(internalTransactionRef, {
            onSuccess: () => {
              setIsPaymentModalOpen(false);
              onSuccessPayment(internalTransactionRef);
            },
            onError: () => {
              setIsPaymentModalOpen(false);
              paymentErrorDispatch(UNEXPECTED_ERROR_ACTION);
            },
          });
        }}
        statusPaymentOffline={statusPaymentOffline}
      />
      <PaymentErrorModal
        closeAllModels={{
          isErrorModalOpen,
          callback: closePaymentModals,
        }}
        isRetry={isRetry}
        modelOpen={isErrorModalOpen}
        onClickModalButton={() => {
          onPaymentComplete(!isRetry);
          closeAllPaymentModals(isRetry || false, type);
        }}
        errorMessages={errorMessages}
        uniqueLink={isUniqueLink}
        buttonText={buttonErrorText}
      />

      <CustomDialog
        open={isConfirmationModalOpen}
        title="Confirm Registration"
        message="Confirm to register and proceed with payment?"
        action={
          <>
            <Button
              id="button-back"
              type="button"
              variant="text"
              color="primary"
              onClick={() => setIsConfirmationModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              id="button-confirm-proceed"
              type="button"
              variant="text"
              color="primary"
              onClick={() => {
                setIsConfirmationModalOpen(false);
                setIsPaymentModalOpen(true);
              }}
            >
              Confirm and Proceed
            </Button>
          </>
        }
      />

      <PaymentApiButton
        buttonClassName={buttonClassName}
        onClickPaymentButton={onClickPaymentButton}
        disabled={disabled}
        buttonText={buttonText}
        fullWidth={fullWidth}
        buttonVariant={buttonVariant}
        setStateHandler={{ setIsDisabled, setIsPaymentModalOpen, setSelectedPaymentMethod }}
        isLoading={isLoading}
      />

      {isVacancyExpired && vacancyExpiredModal}
    </>
  );
};

export default PaymentButton;
